import { motion } from "framer-motion";
import { Star, ShieldCheck, Smartphone } from "lucide-react";
import { SCREENS } from "@/lib/assets";
import { StoreButtons } from "@/components/landing/StoreButtons";
import { useLang } from "@/i18n/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const PhoneFrame = ({ src, alt, className = "" }) => (
  <div
    className={`relative rounded-[2.4rem] border border-white/10 bg-black p-2 shadow-2xl ${className}`}
  >
    <div className="overflow-hidden rounded-[1.9rem]">
      <img src={src} alt={alt} className="block w-full" />
    </div>
  </div>
);

export const Hero = () => {
  const { t } = useLang();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28"
    >
      <div className="absolute inset-0 grid-dots opacity-60" />
      <div className="absolute -top-32 -left-40 h-[460px] w-[460px] rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="absolute top-1/4 -right-40 h-[420px] w-[420px] rounded-full bg-violet-600/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.span
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-emerald-300"
          >
            <Smartphone className="h-3.5 w-3.5" />
            {t.hero.badge}
          </motion.span>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="mt-6 font-outfit text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white"
          >
            {t.hero.titleA}
            <br />
            <span className="text-gradient">{t.hero.titleHighlight}</span>{" "}
            {t.hero.titleB}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-400"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-9"
          >
            <StoreButtons />
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm"
          >
            <div className="flex items-center gap-2">
              <div className="flex text-emerald-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <span className="font-bold text-white">4.8/5</span>
            </div>
            <span className="hidden sm:inline text-slate-600">·</span>
            <span className="inline-flex items-center gap-2 text-slate-400">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              {t.hero.local}
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 -z-10 rounded-[3rem] bg-emerald-500/20 blur-3xl" />
            <div className="animate-float">
              <PhoneFrame
                src={SCREENS.home}
                alt="Écran d'accueil Jos'FIT"
                className="w-[270px] sm:w-[300px]"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
